import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';

/**
 * PDF Styles
 */
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 30,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  text: {
    marginBottom: 8,
    lineHeight: 1.5,
    fontSize: 12,
  },
  h1: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 12,
    marginTop: 16,
    color: '#000000',
  },
  h2: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 14,
    color: '#000000',
  },
  h3: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 12,
    color: '#000000',
  },
  h4: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
    marginTop: 10,
    color: '#000000',
  },
  h5: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 5,
    marginTop: 8,
    color: '#000000',
  },
  h6: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
    marginTop: 6,
    color: '#000000',
  },
  p: {
    marginBottom: 8,
    lineHeight: 1.5,
    fontSize: 12,
  },
  strong: {
    fontWeight: 'bold',
  },
  em: {
    fontStyle: 'italic',
  },
  ul: {
    marginBottom: 8,
    marginLeft: 20,
  },
  ol: {
    marginBottom: 8,
    marginLeft: 20,
  },
  li: {
    marginBottom: 4,
    lineHeight: 1.4,
  },
  bulletPoint: {
    marginBottom: 4,
    lineHeight: 1.4,
    paddingLeft: 20,
    textIndent: -15,
  },
});

/**
 * Helper: Detect header level from AntD class/style or semantic tag
 */
function detectHeaderLevel(element, tagName) {
  // Semantic HTML headers
  if (/^h[1-6]$/.test(tagName)) return tagName;
  // Ant Design secondary header (often used for h4/h5)
  if ((tagName === 'div' || tagName === 'span') && element.className) {
    const className = element.className;
    if (className.includes('ant-typography') && className.includes('ant-typography-secondary')) {
      let style = element.getAttribute('style') || '';
      if (/font-size:\s*20px/.test(style)) return 'h2';
      if (/font-size:\s*16px/.test(style)) return 'h3';
      if (/font-size:\s*14px/.test(style)) return 'h4';
      if (/font-size:\s*13px/.test(style)) return 'h5';
      if (/font-size:\s*12px/.test(style)) return 'h6';
      return 'h4'; // fallback
    }
  }
  return null;
}

/**
 * Helper: Process text for key-value and bullet lines
 */
function processTextContent(text) {
  if (!text) return text;
  // Split into lines to process each line individually
  return text.split(/\r?\n/).map(line => {
    // Skip processing if line starts with bullet point
    if (line.trim().match(/^[•·▪▫‣⁃]/)) {
      return line;
    }
    // Bullet or not, key, colon, value (no leading space)
    return line.replace(/^([\s•]*[\wÁáÉéÍíÓóÚúÑñüÜ\- ]+):([^\s].*)$/, (match, key, value) => {
      return `${key}: ${value}`;
    });
  }).join('\n');
}

/**
 * Helper: Check if text contains bullet points
 */
function hasBulletPoints(text) {
  return text && text.match(/[•·▪▫‣⁃]/);
}



/**
 * Helper: Join date/time lines for #date-time-section
 */
function joinDateTimeLines(element) {
  // For each child div, join its text children into a single line
  const lines = [];
  let lastDivIdx = -1;
  Array.from(element.children).forEach((child, idx) => {
    if (child.tagName && child.tagName.toLowerCase() === 'div') {
      const line = Array.from(child.childNodes)
        .map((n) => n.textContent)
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (line) lines.push(<Text key={idx} style={styles.text}>{line}</Text>);
      lastDivIdx = idx;
    }
  });
  // Render the rest of the children (e.g., markdown) after the date/time divs
  const rest = Array.from(element.childNodes)
    .slice(lastDivIdx + 1)
    .map(convertElementToPDF)
    .filter(Boolean);
  return <View>{[...lines, ...rest]}</View>;
}

/**
 * Main recursive DOM-to-PDF converter
 */
function convertElementToPDF(element) {
  if (!element) return null;

  // Text node
  if (element.nodeType === Node.TEXT_NODE) {
    const text = element.textContent.trim();
    if (!text) return null;
    
    // Check if text contains bullet points
    if (hasBulletPoints(text)) {
      return <Text style={styles.bulletPoint}>{processTextContent(text)}</Text>;
    }
    
    return <Text style={styles.text}>{processTextContent(text)}</Text>;
  }

  // Element node
  if (element.nodeType === Node.ELEMENT_NODE) {
    const tagName = element.tagName.toLowerCase();

    // Special: date-time-section
    if (element.id === 'date-time-section') {
      return joinDateTimeLines(element);
    }

    // Special: key: <strong>value</strong> (or <b>/<i>/<em>)
    const childNodes = Array.from(element.childNodes);
    if (
      childNodes.length >= 2 &&
      childNodes[0].nodeType === Node.TEXT_NODE &&
      /:$/.test(childNodes[0].textContent) &&
      !/:\s+$/.test(childNodes[0].textContent)
    ) {
      const next = childNodes[1];
      if (
        next.nodeType === Node.ELEMENT_NODE &&
        ['strong', 'b', 'em', 'i'].includes(next.tagName.toLowerCase())
      ) {
        // Add a space after colon if missing
        const fixedFirst = childNodes[0].textContent.replace(/:$/, ': ');
        const newChildren = [
          <Text key="fixed-key" style={styles.text}>{fixedFirst}</Text>,
          convertElementToPDF(next),
          ...childNodes.slice(2).map(convertElementToPDF)
        ];
        switch (tagName) {
          case 'li': return <Text style={styles.li}>{newChildren}</Text>;
          case 'p': return <Text style={styles.p}>{newChildren}</Text>;
          default: return <View>{newChildren}</View>;
        }
      }
    }

    // Detect header level (semantic or AntD/inline)
    const headerLevel = detectHeaderLevel(element, tagName);
    if (headerLevel && styles[headerLevel]) {
      const children = Array.from(element.childNodes).map(convertElementToPDF).filter(Boolean);
      return <Text style={styles[headerLevel]}>{children}</Text>;
    }

    // Standard children
    const children = Array.from(element.childNodes).map(convertElementToPDF).filter(Boolean);
    if (children.length === 0) return null;

    // Tag mapping
    switch (tagName) {
      case 'h1': return <Text style={styles.h1}>{children}</Text>;
      case 'h2': return <Text style={styles.h2}>{children}</Text>;
      case 'h3': return <Text style={styles.h3}>{children}</Text>;
      case 'h4': return <Text style={styles.h4}>{children}</Text>;
      case 'h5': return <Text style={styles.h5}>{children}</Text>;
      case 'h6': return <Text style={styles.h6}>{children}</Text>;
      case 'p': return <Text style={styles.p}>{children}</Text>;
      case 'strong':
      case 'b': return <Text style={styles.strong}>{children}</Text>;
      case 'em':
      case 'i': return <Text style={styles.em}>{children}</Text>;
      case 'ul': {
        // For unordered lists, ensure each child (li) gets proper bullet styling
        return <View style={styles.ul}>{children}</View>;
      }
      case 'ol': return <View style={styles.ol}>{children}</View>;
      case 'li': {
        // Check if the list item contains bullet points
        const hasBulletPoint = children.some(child => 
          child && child.props && child.props.children && 
          typeof child.props.children === 'string' && 
          hasBulletPoints(child.props.children)
        );
        
        if (hasBulletPoint) {
          return <Text style={styles.bulletPoint}>{children}</Text>;
        }
        
        // For CSS-styled list items, add a bullet point
        return <Text style={styles.bulletPoint}>• {children}</Text>;
      }
      case 'br': return <Text>{'\n'}</Text>;
      case 'div':
      case 'span':
      case 'section':
      case 'article': return <View>{children}</View>;
      default: return <View>{children}</View>;
    }
  }
  return null;
}

/**
 * Main PDF generation function
 */
export const generatePDFFromRef = async (ref, options = {}) => {
  const container = ref.current;
  if (!container) throw new Error('Container ref is not available');

  const pdfContent = convertElementToPDF(container);
  const MyDocument = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>{pdfContent}</View>
      </Page>
    </Document>
  );

  try {
    const pdfBlob = await pdf(<MyDocument />).toBlob();
    if (options.method === 'blob') {
      return pdfBlob;
    } else {
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = options.filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF');
  }
}; 